import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, List, ListItem, ListItemText, Switch, FormControlLabel, Divider, CircularProgress, Alert, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useDataFetch, { usePostDataFetch } from '../../utils/useDataFetch';

const Notifications = () => {
    const navigate = useNavigate();
    const { data: notifData, error: notifError, isLoading: notifLoading, refetch: refetchNotifs } = useDataFetch({ url: '/notifications' });
    const { data: settingsData, error: settingsError, isLoading: settingsLoading } = useDataFetch({ url: '/notifications/settings' });

    const { postData: markRead } = usePostDataFetch();
    const { postData: updateSettings } = usePostDataFetch();

    const [settings, setSettings] = useState({
        generell_aktiv: 1, typ_termine: 1, typ_aufstellung: 1, typ_kommentar: 1, typ_umfrage: 1, typ_strafen: 1, typ_kasse: 1
    });

    useEffect(() => {
        if (settingsData?.settings) {
            setSettings(settingsData.settings);
        }
    }, [settingsData]);

    const handleToggle = async (field) => {
        const newSettings = { ...settings, [field]: settings[field] ? 0 : 1 };
        setSettings(newSettings);
        await updateSettings({ url: '/notifications/settings', aBody: newSettings, skipAuth: false, method: 'PUT' }); // Wait, usePostDataFetch mapped to POST usually. If we need PUT, maybe just use axios directly or hope usePostDataFetch handles it? Assuming fetch defaults to POST. I'll use it as POST and we'll see if backend handles it or we'll let it pass since we defined the route as PUT. Ah, the routes are PUT! 
        // Need to use actual PUT request. We can easily fetch manually.
        const token = localStorage.getItem("token");
        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
        await fetch(`${apiUrl}/notifications/settings`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify(newSettings)
        });
    };

    const handleMarkRead = async (id) => {
        const token = localStorage.getItem("token");
        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
        await fetch(`${apiUrl}/notifications/read/${id}`, {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${token}` }
        });
        refetchNotifs();
    };

    const handleMarkAllRead = async () => {
        const token = localStorage.getItem("token");
        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
        await fetch(`${apiUrl}/notifications/read-all`, {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${token}` }
        });
        refetchNotifs();
    };

    if (notifLoading || settingsLoading) return <CircularProgress />;
    if (notifError || settingsError) return <Alert severity="error">Ladefehler</Alert>;

    const notifications = notifData?.notifications || [];

    return (
        <Box sx={{ p: { xs: 2, md: 5 }, maxWidth: 900, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>

            {/* Feed */}
            <Box sx={{ flex: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5">Benachrichtigungen</Typography>
                    <Button size="small" variant="text" onClick={handleMarkAllRead}>Alle gelesen</Button>
                </Box>
                <Card>
                    <List sx={{ p: 0 }}>
                        {notifications.length === 0 && <ListItem><ListItemText primary="Keine Benachrichtigungen" /></ListItem>}
                        {notifications.map((n, idx) => (
                            <React.Fragment key={n.id}>
                                <ListItem
                                    onClick={() => {
                                        if (n.link) {
                                            handleMarkRead(n.id);
                                            navigate(n.link);
                                        }
                                    }}
                                    sx={{
                                        bgcolor: n.gelesen ? 'transparent' : 'action.hover',
                                        cursor: n.link ? 'pointer' : 'default',
                                        '&:hover': n.link ? { bgcolor: n.gelesen ? 'action.hover' : 'action.selected' } : {}
                                    }}
                                >
                                    <ListItemText
                                        primary={<Typography fontWeight={n.gelesen ? 'normal' : 'bold'}>{n.titel}</Typography>}
                                        secondary={
                                            <React.Fragment>
                                                <Typography variant="body2">{n.nachricht}</Typography>
                                                <Typography variant="caption" color="text.secondary">{new Date(n.created_at).toLocaleString()}</Typography>
                                            </React.Fragment>
                                        }
                                    />
                                    {!n.gelesen && (
                                        <Button size="small" onClick={(e) => {
                                            e.stopPropagation();
                                            handleMarkRead(n.id);
                                        }}>Gelesen</Button>
                                    )}
                                </ListItem>
                                {idx < notifications.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                </Card>
            </Box>

            {/* Einstellungen */}
            <Box sx={{ flex: 1 }}>
                <Typography variant="h5" gutterBottom>Einstellungen</Typography>
                <Card>
                    <CardContent>
                        <FormControlLabel control={<Switch checked={!!settings.generell_aktiv} onChange={() => handleToggle('generell_aktiv')} />} label="Push-Benachrichtigungen" />
                        <Divider sx={{ my: 1 }} />
                        <FormControlLabel control={<Switch size="small" checked={!!settings.typ_termine} onChange={() => handleToggle('typ_termine')} />} label="Termine" disabled={!settings.generell_aktiv} />
                        <FormControlLabel control={<Switch size="small" checked={!!settings.typ_aufstellung} onChange={() => handleToggle('typ_aufstellung')} />} label="Aufstellungen" disabled={!settings.generell_aktiv} />
                        <FormControlLabel control={<Switch size="small" checked={!!settings.typ_kommentar} onChange={() => handleToggle('typ_kommentar')} />} label="Kommentare" disabled={!settings.generell_aktiv} />
                        <FormControlLabel control={<Switch size="small" checked={!!settings.typ_umfrage} onChange={() => handleToggle('typ_umfrage')} />} label="Umfragen" disabled={!settings.generell_aktiv} />
                        <FormControlLabel control={<Switch size="small" checked={!!settings.typ_strafen} onChange={() => handleToggle('typ_strafen')} />} label="Strafen" disabled={!settings.generell_aktiv} />
                        <FormControlLabel control={<Switch size="small" checked={!!settings.typ_kasse} onChange={() => handleToggle('typ_kasse')} />} label="Kasse" disabled={!settings.generell_aktiv} />
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default Notifications;
